import { gql } from '@apollo/client';

/**
 * ТЗ 0081 — SMS студентам через SMSAPI: общие наборы полей.
 *
 * Одна SMS (`SmsMessageType`) выбирается ОДНИМ фрагментом везде — в журнале,
 * в карточке студента и в ответах мутаций «Повторить» / «Отменить»: запись
 * кэша `SmsMessageType:<id>` у них общая, и ответ мутации обновляет строку
 * на всех экранах сразу, без перечитывания списков.
 */
export const SMS_MESSAGE_FIELDS = gql`
  fragment SmsMessageFields on SmsMessageType {
    id
    topic
    step
    status
    skipReason
    error
    body
    phoneMasked
    segments
    encoding
    cost
    dlrStatus
    deliveredAt
    sentAt
    scheduledFor
    createdAt
    createdByName
    studentId
    studentName
    teacherId
    teacherName
    signerName
    leadId
    leadName
    candidateId
    candidateName
    subjectKind
    subjectId
    subjectLabel
    campaignId
    shortLinkId
    shortLinkUrl
    targetUrl
    humanClicks
    clicks
    canRetry
    canCancel
  }
`;

/** Предпросмотр SMS: как уйдёт, сколько частей, можно ли отправить. */
export const SMS_PREVIEW_FIELDS = gql`
  fragment SmsPreviewFields on SmsPreviewType {
    body
    encoding
    segments
    maxSegments
    tooLong
    withOptOut
    available
    reason
    message
  }
`;

/**
 * Состояние SMS студента. У типа нет `id` — запрос и ответ `sendStudentSms`
 * пишутся в `studentSms(studentId)` руками (см. `useStudentSms`).
 */
export const STUDENT_SMS_STATE_FIELDS = gql`
  ${SMS_MESSAGE_FIELDS}
  fragment StudentSmsStateFields on StudentSmsStateType {
    available
    reason
    phoneMasked
    messages {
      ...SmsMessageFields
    }
    invoices {
      id
      number
      amount
      currency
      dueDate
      daysOverdue
      hasPayUrl
    }
  }
`;

/**
 * Состояние SMS преподавателя (2026-09-22) — как у студента, но вместо
 * фактур документы, которые ждут его подписи. У типа нет `id` — ответ
 * `sendTeacherSms` пишется в `teacherSms(teacherId)` руками
 * (см. `useTeacherSms`).
 */
export const TEACHER_SMS_STATE_FIELDS = gql`
  ${SMS_MESSAGE_FIELDS}
  fragment TeacherSmsStateFields on TeacherSmsStateType {
    available
    reason
    phoneMasked
    messages {
      ...SmsMessageFields
    }
    documents {
      id
      number
      kind
      expiresAt
    }
  }
`;

/** Рассылка по группе и её итог — один набор для списка, опроса и мутаций. */
export const SMS_CAMPAIGN_FIELDS = gql`
  fragment SmsCampaignFields on SmsCampaignType {
    id
    title
    body
    mode
    linkKind
    linkUrl
    groupId
    groupName
    createdAt
    createdByName
    cancelledAt
    counts {
      total
      pending
      sent
      failed
      skipped
      delivered
      retryable
      cost
      finished
    }
  }
`;

/** Настройки SMS и состояние сервера (env) — запрос и ответ сохранения. */
export const SMS_SETTINGS_FIELDS = gql`
  fragment SmsSettingsFields on SmsSettingsType {
    enabled
    remindersEnabled
    senderName
    quietStart
    quietEnd
    dailyCapPerPhone
    allowUnicode
    allowForeignNumbers
    maxSegments
    lowBalancePoints
    legalEntityIds
    updatedAt
    updatedByName
    serverSmsAllowed
    serverRemindersAllowed
    provider
    tokenConfigured
    webhookConfigured
    webhookUrlMasked
    shortlinkBaseUrl
    onlyTo
    canEdit
    optOutSuffix
    defaultOptOutSuffix
    canEditTexts
  }
`;

/**
 * ТЗ 0084: куда ведут ссылки — анкета дофинансирования и LMS (правят Admin
 * и продавцы), сократитель и Documenso (только чтение: инфраструктура).
 * У типа нет `id` — ответ сохранения пишется в корневое поле руками.
 */
export const LINK_ADDRESSES_FIELDS = gql`
  fragment LinkAddressesFields on LinkAddressesType {
    dofinFormUrl
    dofinFormUrlDefault
    dofinFormUrlSaved
    lmsBaseUrl
    lmsBaseUrlDefault
    lmsBaseUrlSaved
    lmsInvoicesUrl
    shortlinkBaseUrl
    documensoBaseUrl
    canEdit
    updatedAt
    updatedByName
  }
`;

/** Баланс и имя отправителя в SMSAPI. */
export const SMS_PROVIDER_STATUS_FIELDS = gql`
  fragment SmsProviderStatusFields on SmsProviderStatusType {
    provider
    tokenConfigured
    checkedAt
    ok
    points
    paymentType
    lowBalance
    lowBalancePoints
    senderName
    senderStatus
    senderActive
    error
    errorCode
  }
`;

/** Результат `test=1` и настоящей тестовой SMS. */
export const SMS_CONNECTION_CHECK_FIELDS = gql`
  fragment SmsConnectionCheckFields on SmsConnectionCheckType {
    ok
    provider
    cost
    parts
    segments
    error
    errorCode
  }
`;

/** Лесенка напоминаний целиком: шаги и тексты, из которых выбирают. */
export const SMS_REMINDER_PLAN_FIELDS = gql`
  fragment SmsReminderPlanFields on ReminderPlanType {
    topic
    enabled
    canEdit
    active
    states
    anchors
    impossibleSteps
    steps {
      id
      step
      anchor
      offsetMinutes
      condition
      templateCode
      enabled
    }
    templates {
      code
      name
      namePl
      body
    }
  }
`;
